import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { VibeUsageTypeLiteralSchema } from "../../../shared/domain/vibe/usage-types";
import {
  VibeAuditOrgCostSafetyStatusReadSchema,
  VibeAuditUsageTypeReadSchema
} from "./read-vocabularies.schema";

/**
 * Shared field shape for the two cost-safety consumption-rollup audit
 * events (AUTO_SUSPENDED, SOFT_LIMIT_WARNING). Both capture the same
 * breach comparison inputs — which usageType crossed its cap, the
 * summed amount, the effective cap, and the billing-period label — so
 * a reader can answer "why did the rollup act on org X?" from the row
 * alone without re-querying the rollup. Extracted so the two events
 * cannot drift apart field-by-field.
 *
 * No UUIDs in this shape: the audit row's `organizationId` column
 * already identifies the tenant, and the rollup decision is org-scoped
 * (no app or deployment attribution).
 */
const VibeAuditCostSafetyBreachShape = {
  usageType: VibeUsageTypeLiteralSchema,
  /// Sum of `VibeUsageEvent.amount` for this `(organizationId,
  /// usageType)` over the billing window. Float to match the
  /// underlying column type (partial minutes / partial MB are
  /// the normal case).
  breachedSum: z.number().finite().nonnegative(),
  /// Effective cap that was crossed — `cap.effectiveCaps()` if a
  /// per-org override row existed, otherwise the matching
  /// `VIBE_PLATFORM_CONSUMPTION_CAP_DEFAULTS` value. Integer at the
  /// boundary (caps are integers) but `.finite()` keeps the
  /// downstream comparison shape stable.
  effectiveCap: z.number().finite().nonnegative(),
  /// `YYYY-MM` of the calendar month the rollup window covered.
  /// Matches `/^\d{4}-(0[1-9]|1[0-2])$/` so the audit feed can
  /// group decisively by billing period without re-deriving from
  /// `createdAt`.
  billingPeriod: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/)
};

/**
 * Emitted by the consumption-rollup cron when it detects that an
 * org breached its effective hard cap for a given `VibeUsageType`
 * over the current billing window. Persisted INSIDE the same
 * `$transaction` as the `VibeOrgCostSafetyState` flip to SUSPENDED
 * (composite-TX adapter) so the audit row + the state row land
 * atomically.
 *
 * RETAINED as a manual-only signal: the auto path no longer emits
 * this (it now flips OK -> WARNING via SOFT_LIMIT_WARNING). SUSPENDED
 * remains an admin escape hatch for genuine abuse, so the event type +
 * payload stay so historical rows still parse and a future manual
 * suspend flow can reuse the shape.
 */
export const VibeAuditCostSafetyAutoSuspendedPayloadSchema = z.object({
  eventType: z.literal("COST_SAFETY_AUTO_SUSPENDED"),
  ...VibeAuditCostSafetyBreachShape
});
export type VibeAuditCostSafetyAutoSuspendedPayload = z.infer<
  typeof VibeAuditCostSafetyAutoSuspendedPayloadSchema
>;

/**
 * Emitted by the consumption-rollup cron when an org crosses its
 * effective SOFT cap for a `VibeUsageType` over the current billing
 * window. This is the soft-limit signal that replaced the hard
 * auto-suspend: the cron flips `VibeOrgCostSafetyState` OK -> WARNING
 * and records this audit row atomically in the same `$transaction`,
 * but WARNING does NOT gate deploys and does NOT trigger rollback —
 * the row is a notify-only marker.
 *
 * Idempotency: the cron only emits on the OK -> WARNING edge. An org
 * already in WARNING (from a prior tick) or in the manual-only
 * SUSPENDED state short-circuits, so re-running the cron every 15 min
 * produces zero duplicate rows. Shares `VibeAuditCostSafetyBreachShape`
 * with AUTO_SUSPENDED so the two cannot drift apart.
 */
export const VibeAuditCostSafetySoftLimitWarningPayloadSchema = z.object({
  eventType: z.literal("COST_SAFETY_SOFT_LIMIT_WARNING"),
  ...VibeAuditCostSafetyBreachShape
});
export type VibeAuditCostSafetySoftLimitWarningPayload = z.infer<
  typeof VibeAuditCostSafetySoftLimitWarningPayloadSchema
>;

/**
 * Emitted by the consumption-rollup cron when it clears a WARNING that was
 * raised in a PRIOR billing period, flipping the org WARNING -> OK so the new
 * period can warn again. Persisted INSIDE the same `$transaction` as that flip,
 * so the retraction and the state change land together or neither does.
 *
 * The retraction of `COST_SAFETY_SOFT_LIMIT_WARNING`, and deliberately named as
 * its twin. Until this event existed the raise was recorded and the retraction
 * was not, so the feed showed a warning being applied and never lifted — the
 * flip was called observable through the state row's `updatedAt`, which says
 * only that a row moved, not what it moved to, and is overwritten by the very
 * next write.
 *
 * NOT a `..._RESUMED`: WARNING never gated deploys, so nothing is unblocked
 * here. `COST_SAFETY_MANUALLY_RESUMED` is a human lifting a real gate; this is
 * the meter withdrawing its own notice. Conflating them would make "was this
 * tenant ever actually blocked?" unanswerable from the event type.
 *
 * No `priorStatus`, unlike the operator events: the write is a compare-and-set
 * on `status = WARNING`, so an emitted row could only have come from WARNING.
 * A second copy of a fact the guard already pins is a second thing that can
 * disagree — the same reason this family carries no `newStatus`.
 */
export const VibeAuditCostSafetySoftLimitClearedPayloadSchema = z.object({
  eventType: z.literal("COST_SAFETY_SOFT_LIMIT_CLEARED"),
  /// `YYYY-MM` of the billing period the reset landed in — the period that is
  /// now free to warn again. Same field and same regex as the two breach
  /// events, so the feed groups all three by period without re-deriving from
  /// `createdAt`.
  billingPeriod: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  /// The state row's `updatedAt` as the reset read it — the exact value the
  /// staleness guard (`updatedAt < windowStart`) tested, and the value this
  /// write destroys by stamping its own.
  ///
  /// It is the one fact here that is genuinely unrecoverable afterwards, and it
  /// is what separates a warning cleared at the next month boundary from one
  /// that sat un-reset for a quarter. Not called `warnedAt`: any write to the
  /// row while it was WARNING moved this timestamp, so it is the age of the
  /// ROW's last write, not provably the instant the warning was raised.
  staleSince: z.string().datetime()
});
export type VibeAuditCostSafetySoftLimitClearedPayload = z.infer<
  typeof VibeAuditCostSafetySoftLimitClearedPayloadSchema
>;

/**
 * Shared field shape for the three OPERATOR-driven cost-safety events
 * (MANUALLY_SUSPENDED / MANUALLY_WARNED / MANUALLY_RESUMED). One event
 * type per TARGET status — mirroring how AUTO_SUSPENDED and
 * SOFT_LIMIT_WARNING already split — so "how many tenants did an
 * operator release this month?" is a GROUP BY over the event type
 * rather than a probe into JSONB. Extracted so the three cannot drift
 * apart field-by-field, the same reason
 * `VibeAuditCostSafetyBreachShape` exists.
 *
 * No `newStatus` field: the event type IS the new status and the entity
 * factory already pins the column to the payload's discriminator. A
 * third copy of one fact is a third thing that can disagree.
 *
 * No breach fields either. The rollup's two events explain a MEASUREMENT
 * (which usageType, what sum, against what cap); these explain a
 * DECISION, and there is no measurement behind one.
 */
const VibeAuditCostSafetyManualTransitionShape = {
  /// The status the org was in when the operator's write landed, read
  /// inside the same transaction as the write itself. Not derivable from
  /// anywhere else: the state row carries only the CURRENT status, so by
  /// the time anyone reads this event the value that makes it a
  /// transition is gone. An absent row counts as OK, per the sparse-table
  /// semantics the read path already uses.
  ///
  /// This is also what distinguishes the two readings of
  /// MANUALLY_WARNED and MANUALLY_RESUMED — from SUSPENDED either one
  /// means the deploy gate came OFF; from OK or WARNING neither does.
  priorStatus: z.enum(DbEnum.VIBE_ORG_COST_SAFETY_STATUS_VALUES),
  /// The rationale the operator set alongside the status, copied in so a
  /// gated tenant's audit row explains itself without re-querying the
  /// state row (which will have moved on by the time anyone reads this).
  /// It is the text the refuse-deploy HTTP message shows the tenant
  /// verbatim.
  ///
  /// Nullable, and it has to be: the entity factory CLEARS the reason for
  /// every non-SUSPENDED status, so MANUALLY_WARNED and MANUALLY_RESUMED
  /// always carry null. For MANUALLY_SUSPENDED the admin write surface
  /// enforces a non-empty reason before the state is built — asserting
  /// that again here would be a second enforcement point that can
  /// disagree with the first. Same posture, same wording, as
  /// `VibeAuditDeploymentRolledBackCostSafetyPayloadSchema`.
  suspendedReason: z.string().nullable()
};

/**
 * Emitted when an operator set an org's cost-safety state to SUSPENDED
 * through the admin surface, gating every deploy for that tenant.
 * Persisted INSIDE the same `$transaction` as the state write, so the
 * gate and the record of who closed it land together or neither does.
 *
 * `actorUserId` IS set on the row — with `DEPLOYMENT_ROLLED_BACK_USER`
 * and `DEPLOYMENT_VERIFICATION_OVERRIDDEN`, one of the few events here
 * that is never a system actor. An unattributable gate change is the
 * exact hole these three event types close.
 */
export const VibeAuditCostSafetyManuallySuspendedPayloadSchema = z.object({
  eventType: z.literal("COST_SAFETY_MANUALLY_SUSPENDED"),
  ...VibeAuditCostSafetyManualTransitionShape
});
export type VibeAuditCostSafetyManuallySuspendedPayload = z.infer<
  typeof VibeAuditCostSafetyManuallySuspendedPayloadSchema
>;

/**
 * Emitted when an operator set an org's cost-safety state to WARNING
 * through the admin surface.
 *
 * Distinct from `COST_SAFETY_SOFT_LIMIT_WARNING`, which the consumption
 * rollup writes with a null actor and a measured breach behind it. A
 * human flagging a tenant and a meter tripping are different facts;
 * conflating them would make "did we warn this tenant, or did the
 * meter?" answerable only by testing `actorUserId` for null — the same
 * category error as reading HEALTHY to mean SERVED.
 */
export const VibeAuditCostSafetyManuallyWarnedPayloadSchema = z.object({
  eventType: z.literal("COST_SAFETY_MANUALLY_WARNED"),
  ...VibeAuditCostSafetyManualTransitionShape
});
export type VibeAuditCostSafetyManuallyWarnedPayload = z.infer<
  typeof VibeAuditCostSafetyManuallyWarnedPayloadSchema
>;

/**
 * Emitted when an operator set an org's cost-safety state to OK through
 * the admin surface. From SUSPENDED this is THE RELEASE — the row whose
 * absence is why this family exists.
 *
 * Three production orgs were auto-suspended in July and released by a
 * single statement; the audit table holds three
 * `COST_SAFETY_AUTO_SUSPENDED` rows and nothing at all for the release,
 * so the only surface that could answer "is this tenant still gated?"
 * was a direct database session. Those historical rows are NOT
 * backfilled: their actor and their instant were never observed, and
 * inventing them would make the feed lie in a new way.
 */
export const VibeAuditCostSafetyManuallyResumedPayloadSchema = z.object({
  eventType: z.literal("COST_SAFETY_MANUALLY_RESUMED"),
  ...VibeAuditCostSafetyManualTransitionShape
});
export type VibeAuditCostSafetyManuallyResumedPayload = z.infer<
  typeof VibeAuditCostSafetyManuallyResumedPayloadSchema
>;

/**
 * The READ side of this family's branches: what the audit feed's readers parse.
 * Each one widens only its enum fields, to the lenient read vocabularies in
 * `read-vocabularies.schema.ts` — a value a newer backend writes reaches an
 * older reader as that field's unlisted value instead of failing the feed page.
 * The emitting branches above stay strict.
 */
const VibeAuditCostSafetyBreachRead = { usageType: VibeAuditUsageTypeReadSchema };
const VibeAuditCostSafetyManualTransitionRead = {
  priorStatus: VibeAuditOrgCostSafetyStatusReadSchema
};

export const VibeAuditCostSafetyAutoSuspendedReadPayloadSchema =
  VibeAuditCostSafetyAutoSuspendedPayloadSchema.extend(VibeAuditCostSafetyBreachRead);
export const VibeAuditCostSafetySoftLimitWarningReadPayloadSchema =
  VibeAuditCostSafetySoftLimitWarningPayloadSchema.extend(VibeAuditCostSafetyBreachRead);
export const VibeAuditCostSafetyManuallySuspendedReadPayloadSchema =
  VibeAuditCostSafetyManuallySuspendedPayloadSchema.extend(VibeAuditCostSafetyManualTransitionRead);
export const VibeAuditCostSafetyManuallyWarnedReadPayloadSchema =
  VibeAuditCostSafetyManuallyWarnedPayloadSchema.extend(VibeAuditCostSafetyManualTransitionRead);
export const VibeAuditCostSafetyManuallyResumedReadPayloadSchema =
  VibeAuditCostSafetyManuallyResumedPayloadSchema.extend(VibeAuditCostSafetyManualTransitionRead);
